import { TuiNotificationService } from '@taiga-ui/core';
/* eslint-disable max-lines */
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { GalleryService } from '@modules/gallery/services/gallery.service';
import { getSadmRoute } from '@modules/sadm/utils/get-sadm-route';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Media, PublicUser } from '@pixacare/domain';
import { FlattenedSequence, Sequence } from '@pixacare/dtos';
import { PagedResponse } from '@pixacare/shared';
import { sadmDashboardCacheBuster$ } from '@services/cache/sadm-dashboard-cache-buster';
import { MediaHttpService } from '@services/http/media.http.service';
import { SequenceHttpService } from '@services/http/sequence.http.service';
import { PaginationOptions } from '@shared/models/pagination/pagination-options';
import { selectDepartmentId } from '@shared/store/router/router.selectors';
import { catchError, concat, concatMap, filter, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { clientsActions } from '../clients/clients.actions';
import { labelsActions } from '../labels/labels.actions';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { patientsActions } from '../patients/patients.actions';
import { sadmActions } from '../sadm/sadm.actions';
import { usersActions } from '../users/users.actions';
import { sequencesActions } from './sequences.actions';

const getSequencesUsers = (sequences: FlattenedSequence[]): PublicUser[] =>
  sequences.flatMap((sequence) => [sequence.createdByInstance, sequence.reviewedByInstance]).filter((user) => !!user);

const handlePreviousNavigation = (
  collection: PagedResponse<FlattenedSequence>,
  clientCode: string,
  options?: PaginationOptions,
): Action[] => [
  sequencesActions.loadSequencesEntities({
    clientCode,
    reset: options?.reset,
    collection,
  }),
  sequencesActions.getPreviousUserSequencesSuccess({
    clientCode,
    collection,
  }),
];

const handleNextNavigation = (
  collection: PagedResponse<FlattenedSequence>,
  clientCode: string,
  options?: PaginationOptions,
): Action[] => [
  sequencesActions.loadSequencesEntities({ clientCode, collection }),
  sequencesActions.getNextUserSequencesSuccess({
    clientCode,
    reset: options?.reset,
    collection,
  }),
];

@Injectable()
export class SequencesEffects {
  private readonly actions$ = inject(Actions);
  private readonly sequenceService = inject(SequenceHttpService);
  private readonly mediaService = inject(MediaHttpService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly translocoService = inject(TranslocoService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly galleryService = inject(GalleryService);
  private readonly departmentId = inject(Store).selectSignal(selectDepartmentId);

  getUserSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getUserSequence),
      mergeMap(({ clientCode, sequenceId }) =>
        this.sequenceService.getSequence(clientCode, sequenceId).pipe(
          switchMap((sequence: FlattenedSequence) => [
            sequencesActions.loadSequenceEntities({ sequence, clientCode }),
            sequencesActions.updateFlattenedSequenceSuccess({ clientCode, sequence }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getPreviousUserSequences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getPreviousUserSequences),
      mergeMap(({ clientCode, options }) =>
        this.sequenceService.getSequences(clientCode, options).pipe(
          switchMap((collection: PagedResponse<FlattenedSequence>) =>
            handlePreviousNavigation(collection, clientCode, options),
          ),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  getNextUserSequences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getNextUserSequences),
      mergeMap(({ clientCode, options }) =>
        this.sequenceService.getSequences(clientCode, options).pipe(
          switchMap((collection: PagedResponse<FlattenedSequence>) =>
            handleNextNavigation(collection, clientCode, options),
          ),
          catchError((error: HttpErrorResponse) => [
            lifeCycleActions.loadError({ error }),
            ...(error.status === HttpStatusCode.Forbidden
              ? [
                  notificationsActions.error({
                    title: this.translocoService.translate('Les séquences ne peuvent pas être chargées sur ce client.'),
                    message: this.translocoService.translate("Vous n'avez pas accès aux séquences de ce client."),
                  }),
                ]
              : []),
          ]),
        ),
      ),
    ),
  );

  updateSequenceMedia$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.updateSequenceMedia),
      filter(({ mediaId }) => !!mediaId),
      mergeMap(({ clientCode, sequenceId, mediaId }) =>
        this.mediaService.getMedia(clientCode, mediaId).pipe(
          switchMap((updatedMedia: Media) => [
            sequencesActions.updateSequenceMediaSuccess({ sequenceId, updatedMedia }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  updateSequenceLabels$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.updateSequenceLabels),
      mergeMap(({ clientCode, userId, sequenceId, existingLabels, createdLabels }) =>
        this.sequenceService.updateSequenceLabels(clientCode, userId, sequenceId, existingLabels, createdLabels).pipe(
          switchMap((sequence: FlattenedSequence) => [
            labelsActions.addLabels({
              labels: sequence.labels,
              isUsedByUser: sequence.createdByInstance?.id === userId,
            }),
            sequencesActions.updateFlattenedSequenceSuccess({
              clientCode: sequence.clientCode,
              sequence,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  createSequenceSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.createSequenceSuccess),
      mergeMap(({ clientCode, sequence }) => [
        labelsActions.addLabels({ labels: sequence.labels, isUsedByUser: true }),
        ...(sequence.patientInstance
          ? [patientsActions.updatePatientSuccess({ clientCode, patient: sequence.patientInstance })]
          : []),
        ...(sequence.sequenceInstance.sadmEntityId
          ? [sadmActions.updateSadmEntitySuccess({ sadmEntity: sequence.sadmEntity })]
          : []),
      ]),
    ),
  );

  uploadSequenceMedias$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.uploadSequenceMedias),
      mergeMap(({ clientCode, sequenceId, medias }) => {
        const loadingToastr = this.alertService
          .open(this.translocoService.translate('Veuillez patienter quelques instants ...'), {
            label: this.translocoService.translate('Synchronisation des photos ...'),
            appearance: 'info',
            closable: false,
            autoClose: 10000,
          })
          .subscribe();

        return this.sequenceService.attachSequenceMedias(clientCode, sequenceId, medias).pipe(
          switchMap((attachedMedias: Media[]) => {
            loadingToastr.unsubscribe();
            sadmDashboardCacheBuster$.next();
            return [
              sequencesActions.attachSequenceMediasSuccess({ clientCode, sequenceId, medias: attachedMedias }),
              notificationsActions.success({
                title: this.translocoService.translate('Séquence synchronisée.'),
                message: this.translocoService.translate('Votre séquence de photographies est désormais sécurisée.'),
              }),
            ];
          }),
          catchError((error: Error) => {
            loadingToastr.unsubscribe();
            return this.sequenceService.getSequence(clientCode, sequenceId).pipe(
              switchMap((updatedSequence: FlattenedSequence) => [
                sequencesActions.updateFlattenedSequenceSuccess({ clientCode, sequence: updatedSequence }),
                ...this.uploadSequenceMediasErrorActions({
                  error,
                  message: 'Veuillez réessayer de télécharger les médias manquants',
                }),
              ]),
              catchError((getSequenceError: Error) =>
                this.uploadSequenceMediasErrorActions({
                  error: getSequenceError,
                  message:
                    'Nous avons rencontré une erreur lors de la récupération de votre séquence.' +
                    ' Veuillez rafraichir la page.',
                }),
              ),
            );
          }),
        );
      }),
    ),
  );

  addSequenceMedia$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.addSequenceMedia),
      mergeMap(({ clientCode, sequenceId, mediaId }) =>
        this.mediaService.getMedia(clientCode, mediaId).pipe(
          switchMap((updatedMedia: Media) => [sequencesActions.addSequenceMediaSuccess({ sequenceId, updatedMedia })]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  addSequenceMediaSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sequencesActions.addSequenceMediaSuccess),
        tap(() => {
          this.galleryService.navigateToLastItem();
        }),
      ),
    { dispatch: false },
  );

  patchSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.patchSequence),
      mergeMap(({ sequence, clientCode }) =>
        this.sequenceService.patchSequence(clientCode, sequence).pipe(
          switchMap((updatedSequence: Sequence) => [
            sequencesActions.updateSequenceSuccess({
              clientCode,
              updatedSequence,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  updateSequencePatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.updateSequencePatient),
      mergeMap(({ clientCode, sequenceId, patient }) =>
        this.sequenceService.updateSequencePatient(clientCode, sequenceId, patient).pipe(
          switchMap((sequence: FlattenedSequence) => [
            sequencesActions.updateFlattenedSequenceSuccess({
              clientCode,
              sequence,
            }),
            patientsActions.updatePatientSuccess({
              clientCode,
              patient: sequence.patientInstance,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  moveSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.moveSequence),
      concatMap(({ fromClientCode, toClientCode, sequenceId, overridePatient }) =>
        concat(
          of(
            notificationsActions.info({
              title: this.translocoService.translate('Nous transférons vos données de façon sécurisée.'),
            }),
          ),
          this.sequenceService.moveSequence(fromClientCode, toClientCode, sequenceId, overridePatient).pipe(
            switchMap(() => [
              sequencesActions.deleteSequenceSuccess({ sequenceId }),
              notificationsActions.success({
                title: this.translocoService.translate('Vos données ont été correctement déplacées.'),
                message: this.translocoService.translate('Séquence deplacée'),
              }),
            ]),
            catchError((error: Error) => [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                title: this.translocoService.translate(
                  `La séquence n'a pas été déplacée, merci de réessayer plus tard.`,
                ),
                message: this.translocoService.translate('Erreur lors du déplacement'),
              }),
            ]),
          ),
        ),
      ),
    ),
  );

  deleteSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.deleteSequence),
      mergeMap((payload) =>
        this.sequenceService.deleteSequence(payload.clientCode, payload.sequenceId).pipe(
          switchMap(() => [
            sequencesActions.deleteSequenceSuccess({ sequenceId: payload.sequenceId }),
            notificationsActions.success({
              title: this.translocoService.translate('La séquence a bien été supprimée.'),
              message: this.translocoService.translate('Votre séquence a été supprimée avec succès.'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu supprimer la séquence."),
            }),
          ]),
        ),
      ),
    ),
  );

  attachSequenceMedias$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.attachSequenceMedias),
      mergeMap(({ clientCode, sequenceId, medias }) =>
        this.sequenceService.attachSequenceMedias(clientCode, sequenceId, medias).pipe(
          switchMap((sequenceMedias: Media[]) => [
            sequencesActions.attachSequenceMediasSuccess({
              clientCode,
              sequenceId,
              medias: sequenceMedias,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  // Every real media attach (dialog, woundtrack, sadm) converges here, so quota tracking is centralized.
  updateDataConsumptionOnAttachSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.attachSequenceMediasSuccess),
      map(({ clientCode, medias }) => clientsActions.updateDataConsumption({ clientCode, medias })),
    ),
  );

  deleteSequenceMedia$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.deleteSequenceMedia),
      mergeMap(({ clientCode, sequenceId, mediaId }) =>
        this.sequenceService.deleteSequenceMedia(clientCode, sequenceId, mediaId).pipe(
          switchMap(() => [sequencesActions.deleteSequenceMediaSuccess({ sequenceId, mediaId })]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  loadSequences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.loadSequences),
      mergeMap(({ data }) => [
        sequencesActions.loadSequencesEntities({
          clientCode: null,
          collection: {
            data,
          } as unknown as PagedResponse<FlattenedSequence>,
        }),
      ]),
    ),
  );

  loadSequenceEntities$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.loadSequenceEntities),
      mergeMap(({ sequence }) => [
        patientsActions.updatePatientsSuccess({
          clientCode: sequence.clientCode,
          patients: [sequence.patientInstance],
        }),
        labelsActions.addLabels({
          labels: sequence.labels,
          isUsedByUser: sequence.createdByInstance?.id === this.authenticationService.user.id,
        }),
        usersActions.updateUsers({ users: getSequencesUsers([sequence]) }),
      ]),
    ),
  );

  loadSequencesEntities$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.loadSequencesEntities),
      mergeMap(({ clientCode, collection }) => {
        const patientActions =
          clientCode !== null
            ? [
                patientsActions.updatePatientsSuccess({
                  clientCode,
                  patients: collection.data.map((sequence) => sequence.patientInstance),
                }),
              ]
            : collection.data.map((sequence) =>
                patientsActions.updatePatientSuccess({
                  clientCode: sequence.clientCode,
                  patient: sequence.patientInstance,
                }),
              );

        return [
          ...collection.data.map((sequence) =>
            labelsActions.addLabels({
              labels: sequence.labels,
              isUsedByUser: sequence.createdByInstance?.id === this.authenticationService.user.id,
            }),
          ),
          usersActions.updateUsers({ users: getSequencesUsers(collection.data) }),
          ...patientActions,
        ];
      }),
    ),
  );

  addSequenceAnalysis$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.addSequenceAnalysis),
      mergeMap(({ sequenceId, clientCode, payload, redirect = false }) => {
        const loadingToastr = this.alertService
          .open(this.translocoService.translate('Veuillez patienter'), {
            label: this.translocoService.translate("Création de l'analyse en cours ..."),
            appearance: 'info',
            autoClose: 10000,
            closable: false,
          })
          .subscribe();

        return this.sequenceService.addSequenceAnalysis(sequenceId, clientCode, payload).pipe(
          switchMap((sequence: Sequence) => {
            loadingToastr.unsubscribe();

            return [
              sequencesActions.updateSequenceSuccess({
                clientCode,
                updatedSequence: sequence,
              }),
              ...(redirect
                ? [
                    lifeCycleActions.redirect({
                      url: getSadmRoute(clientCode, this.departmentId(), sequence.sadmEntityId).join('/'),
                    }),
                  ]
                : []),
            ];
          }),
          catchError((error: Error) => {
            loadingToastr.unsubscribe();
            console.error('Error while adding analysis to sequence', error);

            return [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                title: this.translocoService.translate("Impossible d'ajouter l'analyse."),
                message: this.translocoService.translate("Une erreur s'est produite."),
              }),
            ];
          }),
        );
      }),
    ),
  );

  editSequenceAnalysis$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.editSequenceAnalysis),
      mergeMap((payload) =>
        this.sequenceService.editSequenceAnalysis(payload).pipe(
          switchMap((sequence: Sequence) => [
            sequencesActions.updateSequenceSuccess({
              clientCode: payload.clientCode,
              updatedSequence: sequence,
            }),
          ]),
        ),
      ),
    ),
  );

  deleteSequenceAnalysis$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.deleteSequenceAnalysis),
      mergeMap((payload) =>
        this.sequenceService.deleteSequenceAnalysis(payload).pipe(
          switchMap((sequence: Sequence) => [
            sequencesActions.updateSequenceSuccess({
              clientCode: payload.clientCode,
              updatedSequence: sequence,
            }),
          ]),
        ),
      ),
    ),
  );

  getSequenceAnalysis$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getSequenceAnalysis),
      mergeMap((payload) =>
        this.sequenceService
          .getSequence(payload.clientCode, payload.sequenceId, {
            forms: true,
          })
          .pipe(
            switchMap((sequence: FlattenedSequence) => [
              sequencesActions.updateSequenceSuccess({
                clientCode: payload.clientCode,
                updatedSequence: sequence.sequenceInstance,
              }),
              ...(sequence.sadmEntity
                ? [
                    sadmActions.getSadmEntitySuccess({
                      sadmEntity: sequence.sadmEntity,
                    }),
                  ]
                : []),
            ]),
          ),
      ),
    ),
  );

  getSequence$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getSequence),
      mergeMap(({ clientCode, sequenceId }) =>
        this.sequenceService.getSequence(clientCode, sequenceId).pipe(
          map((sequence: FlattenedSequence) =>
            sequencesActions.getSequenceSuccess({
              clientCode,
              sequenceId,
              sequence,
            }),
          ),
        ),
      ),
    ),
  );

  putDepartmentIds$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.putSequenceDepartments),
      mergeMap(({ clientCode, sequenceId, departmentIds }) =>
        this.sequenceService.updateDepartmentIds({ sequenceId, clientCode, departmentIds }).pipe(
          switchMap((sequence: FlattenedSequence) => [
            sequencesActions.updateFlattenedSequenceSuccess({
              clientCode,
              sequence,
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  private uploadSequenceMediasErrorActions({ error, message }: { error: Error; message: string }): Action[] {
    return [
      lifeCycleActions.loadError({ error }),
      notificationsActions.error({
        title: this.translocoService.translate('Séquence partiellement synchronisée'),
        message: this.translocoService.translate(message),
      }),
    ];
  }
}
